import _ from 'lodash';
import PropTypes from 'prop-types';
import { Typeahead } from 'react-bootstrap-typeahead';

import { quickAdjustmentPropTypes } from 'common/propTypes';
import { QUICK_ADJUSTMENT_TRIGGERS, QUICK_ADJUSTMENT_ACTIONS } from 'v2/common/constants/index';
import QuickAdjustmentDelayBy from './QuickAdjustmentDelayBy';
import { isCaseBasedAction } from './Utils';
import { useMemo, useRef } from 'react';

const propTypes = {
  quickAdjustment: quickAdjustmentPropTypes.isRequired,
  TypeaheadOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  intentId: PropTypes.string.isRequired,
};

const defaultProps = {};

const renderTrigger = (trigger, onUpdateKey, intentId) => {
  return (
    <>
      <div className="d-flex align-items-center">
        <b className="text-color">IF</b>
        <span className="leg-label-ribbon">
          <div className="wrapper">
            <div className="badge-secondary">Leg {intentId}</div>
          </div>
        </span>
      </div>
      <select
        className="quick-trigger"
        value={trigger}
        onChange={(e) => onUpdateKey('trigger', e.target.value)}
      >
        {_.map(QUICK_ADJUSTMENT_TRIGGERS, (t) => (
          <option value={t.value} key={t.value}>
            {t.displayName}
          </option>
        ))}
      </select>
    </>
  );
};

const QuickAdjustmentTypeahead = ({
  quickAdjustment,
  TypeaheadOptions,
  index,
  onUpdate,
  onDelete,
  intentId,
}) => {
  const { trigger, action, targets, delayBy } = quickAdjustment;

  const typeaheadRef = useRef(null);
  const renderLabel = useMemo(() => (isCaseBasedAction(action) ? 'Case' : 'Leg'), [action]);
  const isPrivilegedUser = _.get(window, 'isPrivilegedUser', false);

  const onUpdateKey = (key, value) => {
    onUpdate(index, { ...quickAdjustment, [key]: value });
  };
  const selectedTargetOptions = _.map(targets, (target) => ({
    id: target,
    label: isCaseBasedAction(action) ? `Case ${_.toNumber(target) + 1}` : `Leg ${target}`,
    target: isCaseBasedAction(action) ? 'case' : 'leg',
  }));
  const onTargetsUpdate = (newSelections) => {
    return onUpdateKey('targets', _.map(newSelections, 'id'));
  };

  const handelRenderActionOnChange = (e) => {
    const { value } = e.target;
    onUpdateKey('action', value);

    const currentTargetType = _.head(selectedTargetOptions)?.target;
    const newActionType = isCaseBasedAction(value) ? 'case' : 'leg';

    if (currentTargetType && currentTargetType !== newActionType && typeaheadRef.current) {
      typeaheadRef.current.clear();
      onUpdate(index, { ...quickAdjustment, action: value, targets: [] });
    }
  };

  const renderAction = () => {
    return (
      <>
        <b className="text-color">Then</b>
        <select className="quick-action" value={action} onChange={(e) => handelRenderActionOnChange(e)}>
          {_.map(QUICK_ADJUSTMENT_ACTIONS, (a) =>
            isCaseBasedAction(a.value) && !isPrivilegedUser ? null : (
              <option value={a.value} key={a.value}>
                {a.displayName}
              </option>
            )
          )}
        </select>
      </>
    );
  };

  return (
    <div className="quick-adjustment mb-3" key={index}>
      {renderTrigger(trigger, onUpdateKey, intentId)}
      {renderAction()}
      <Typeahead
        id="quick-adjustments-targets"
        ref={typeaheadRef}
        className="quick-targets"
        labelKey="label"
        multiple
        onChange={onTargetsUpdate}
        options={TypeaheadOptions}
        placeholder={`Select ${renderLabel}...`}
        defaultSelected={selectedTargetOptions}
        positionFixed
        filterBy={(option) => {
          if (!selectedTargetOptions) return true;
          return !selectedTargetOptions.some((selected) => selected.id === option.id);
        }}
      />
      <QuickAdjustmentDelayBy value={delayBy} onChange={onUpdateKey} />
      <button
        className="btn btn-outline-secondary me-2 quick-adjustmnet-delete"
        type="button"
        onClick={() => onDelete(index)}
      >
        <i className="fa-light fa-trash-can" aria-disabled="false"></i>
      </button>
    </div>
  );
};

QuickAdjustmentTypeahead.propTypes = propTypes;
QuickAdjustmentTypeahead.defaultProps = defaultProps;

export default QuickAdjustmentTypeahead;
